/**
 * PersonsPage — person registry list (henkilörekisteri, #763/#764)
 *
 * Uses the shared list-page components and DataTable so the registry matches
 * the other Staff Portal entity lists (issue #879, epic #877).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { usePagedPersons, type Person } from '@/hooks/usePersons';
import { useCollectionSync } from '@/hooks/useEntitySync';
import { queryKeys } from '@/hooks/queryKeys';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { useUrlSearchParam } from '@/hooks/useUrlSearchParam';
import {
  DataTable,
  type DataTableColumn,
  EntityListResults,
  ListPageHeader,
  SearchField,
} from '@/components/common';

export default function PersonsPage() {
  const { t } = useTranslation('persons');
  const navigate = useNavigate();

  // Real-time person list updates (#2100 Gate 1). This is what makes an
  // assistant-created customer appear on a persons list open in ANOTHER tab,
  // which is the gate's criterion: a second screen that never went through
  // `useAssistantChat.confirmAction`.
  useCollectionSync('person', {
    relevantScopes: ['details'],
    invalidationKeys: [{ queryKey: queryKeys.persons.lists() }],
  });

  const [search, setSearch] = useUrlSearchParam();
  const debouncedSearch = useDebounce(search, 300);
  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    rows: persons,
    total,
    isLoading,
    isError,
    error,
    refetch,
    sort,
    setSort,
    pagination,
  } = usePagedPersons(debouncedSearch ? { q: debouncedSearch } : {});

  const columns: DataTableColumn<Person>[] = [
    {
      header: t('table.name'),
      sortKey: 'name',
      renderCell: (person) => (
        <Typography variant="body2" fontWeight={500}>
          {person.firstName} {person.lastName}
        </Typography>
      ),
    },
    {
      header: t('table.phone'),
      sortKey: 'phone',
      renderCell: (person) => <Typography variant="body2">{person.phone ?? '—'}</Typography>,
    },
    {
      header: t('table.email'),
      sortKey: 'email',
      renderCell: (person) => <Typography variant="body2">{person.email ?? '—'}</Typography>,
    },
    {
      header: '',
      align: 'center',
      width: 56,
      renderCell: (person) => (
        <Tooltip title={t('view')}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/parties/persons/${person.id}`);
            }}
            aria-label={t('view')}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const renderMobileCard = (person: Person) => (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {person.firstName} {person.lastName}
      </Typography>
      {person.phone && (
        <Typography variant="body2" color="text.secondary">
          {person.phone}
        </Typography>
      )}
      {person.email && (
        <Typography variant="body2" color="text.secondary">
          {person.email}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box>
      <ListPageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            {t('addPerson')}
          </Button>
        }
      />

      <SearchField value={search} onChange={setSearch} placeholder={t('searchPlaceholder')} />

      <EntityListResults
        loading={isLoading}
        isError={isError}
        errorText={t('errorLoading')}
        error={error}
        onRefetch={() => void refetch()}
        resultCountText={t('searchResultCount', { count: total })}
        pagination={pagination}
      >
        <DataTable<Person>
          columns={columns}
          data={persons}
          getRowKey={(person) => person.id}
          onRowClick={(person) => navigate(`/parties/persons/${person.id}`)}
          getRowHref={(person) => `/parties/persons/${person.id}`}
          renderMobileCard={renderMobileCard}
          loading={isLoading}
          emptyMessage={t('empty')}
          emptyDescription={t('emptyDesc')}
          sortKey={sort?.sortBy ?? 'name'}
          sortDirection={sort?.sortDir ?? 'asc'}
          onSortChange={(sortKey, sortDirection) => {
            setSort({ sortBy: sortKey, sortDir: sortDirection });
          }}
          ariaLabel={t('title')}
        />
      </EntityListResults>

      <PersonFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(person) => navigate(`/parties/persons/${person.id}`)}
      />
    </Box>
  );
}
